import { useState, useEffect } from "react";
import { Provider } from "react-redux";
import createAppStore from "./redux/store";
import axios from "axios";
import FallbackLoading from "./components/loader/FallbackLoading";
import ServerError from "./components/error/ServerError";
import App from "./App";
import { getTitleFromRoute } from "./utils/docTitle";
import { Helmet } from "react-helmet";
import { useLocation } from "react-router-dom";
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const BASE_URL = process.env.REACT_APP_API_URL || "/api";

const AppContainer = () => {
  const location = useLocation();
  const [store, setStore] = useState(null);
  const [loading, setLoading] = useState(true);
  const [retrying, setRetrying] = useState(false);
  const [error, setError] = useState(null);
  const [retryToken, setRetryToken] = useState(0);

  // Both checks must finish before we lift the loading gate — clearing it as
  // soon as either one resolves let the app render with `store` still null
  // (or before the auth check inside createAppStore had settled).
  useEffect(() => {
    let isMounted = true;

    const boot = async () => {
      setError(null);
      const [serverCheck, storeResult] = await Promise.allSettled([
        axios.get(`${BASE_URL}/check_server_status`),
        createAppStore(),
      ]);

      if (!isMounted) return;

      if (storeResult.status === "fulfilled") {
        setStore(storeResult.value);
      }

      if (storeResult.status === "rejected") {
        console.error("Error initializing the app:", storeResult.reason);
        setError(true);
      } else if (serverCheck.status === "rejected") {
        console.error("Server status check failed:", BASE_URL, serverCheck.reason);
        setError(true);
      }

      setLoading(false);
      setRetrying(false);
    };

    boot();

    return () => {
      isMounted = false;
    };
  }, [retryToken]);

  // Once the first boot has settled, a failed retry keeps the error page
  // mounted (only its button shows a "retrying" state) instead of flashing
  // back to the full-screen loader on every attempt.
  if (loading) return <FallbackLoading />;

  if (error || !store) {
    return (
      <ServerError
        retrying={retrying}
        onRetry={() => {
          setRetrying(true);
          setRetryToken((token) => token + 1);
        }}
      />
    );
  }

  return (
    <Provider store={store}>
      <Helmet>
        <title>{getTitleFromRoute(location.pathname)}</title>
      </Helmet>
      <App />
      <ToastContainer limit={5} />
    </Provider>
  );
};

export default AppContainer;
